"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.entryTimeCorrectionSchema = exports.entryTimeCorrectionInputSchema = exports.entryTimeCorrectionParamsSchema = exports.correctionSchema = exports.correctionInputSchema = exports.correctionParamsSchema = void 0;
// `RPM-006` — a correction is a NEW reading pointing at the one it supersedes, and nothing is
// destroyed (`J-262`).
//
// THE CHAIN IS THE RECORD. `vital_readings.corrects_reading_id` names the superseded row and
// `superseded_at` marks it; the original is never edited and never deleted, which is the
// requirement's own obligation: "the superseded reading id; the original row is never edited and
// never deleted". A reader walks the chain in both directions afterwards.
//
// THE VALUE IS AS TYPED, like every other reading (`RPM-002`). The correction carries the text the
// nurse entered and the server extracts, resolves and classifies it exactly as a first entry —
// there is no second path through which a number reaches `vital_readings`.
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
const rpm_entries_js_1 = require("./rpm-entries.js");
/**
 * The reading being corrected, named in the path (`RPM-101`'s API row).
 *
 * `POST /api/rpm/v1/readings/:readingId/corrections`. An id the caller's tenants do not hold is
 * `404` and the sentence says nothing more: whether the row exists elsewhere is not a fact this
 * surface reveals. A UUID in a path is what `.claude/rules/phi.md` rule 2 permits — an opaque id,
 * never a name, a date of birth or an MRN.
 */
exports.correctionParamsSchema = zod_1.z.strictObject({ readingId: zod_1.z.uuid() });
exports.correctionInputSchema = zod_1.z
    .strictObject({
    /** What she meant to type. The same field a first entry carries, refused empty by the same rule. */
    valueText: zod_1.z.string().min(1).max(200),
    /** The unit as typed, omitted when she left it empty — absent means the definition's own code. */
    unit: zod_1.z.string().min(1).max(40).optional(),
    /**
     * WHAT MAY BE CORRECTED IS A CLOSED LIST, and `RPM-101` draws the line: "The **value**, its
     * **unit**, the **local date and time**, the **vital type** and an SpO₂ reading's **oxygen
     * status** are corrections: the reading is about the same event and the entry got a fact about it
     * wrong. The **facility**, the **resident** and the **origin** are not: those were picks, and
     * changing one does not describe the same reading."
     *
     * So there is no `facilityId` and no `careasePatientId` here, and this being a strict object is
     * what refuses one: a body naming either is `400` rather than a body whose extra field is
     * quietly dropped. A mis-picked resident is re-entered through `RPM-097`, not corrected here.
     *
     * Each of the four below is absent when she left that fact alone, and absent means the
     * superseded reading's own value carries over.
     */
    takenAtLocalDate: zod_1.z.string().min(1).max(40).optional(),
    takenAtLocalTime: zod_1.z.string().min(1).max(40).optional(),
    /** The vital she meant. A grouped blood pressure is one control and is not corrected row-wise. */
    vitalCode: zod_1.z.enum(rpm_entries_js_1.SINGLE_ROW_VITALS).optional(),
    /**
     * Required where the corrected reading is an oxygen saturation, because `SpO2`'s bands are
     * scoped to it (`cb_oxygen_scope`) and a correction that dropped the answer could not be scored.
     */
    oxygenStatus: zod_1.z.enum(["ROOM_AIR", "ON_OXYGEN", "UNKNOWN"]).optional(),
    /**
     * People she adds to a workup the correction opens, never required.
     *
     * Since `CR-43` the routing names who is told (`RPM-099`), so this list is never required: a
     * correction that opens a workup opens it naming nobody unless a client sends people here, and
     * the dialog sends none (`J-492`). Where the reading ALREADY belonged to a workup, the person
     * that workup named carries over.
     */
    notifyPeople: zod_1.z.array(rpm_entries_js_1.notifyPersonSchema).max(rpm_entries_js_1.NOTIFY_PEOPLE_MAX).optional(),
})
    // NO `id`, DELIBERATELY, exactly as `vitalsEntryInputSchema` carries none. A body schema that
    // names a component collides with the response registry: `fastify-type-provider-zod` builds an
    // input registry and an output registry and refuses a name that resolves in both.
    .meta({
    description: "One correction of one reading: the value she meant, pointing at the one she typed",
});
exports.correctionSchema = zod_1.z
    .strictObject({
    /** The new row. It carries `correctsReadingId` and is the reading that now stands. */
    vitalReadingId: zod_1.z.uuid(),
    correctsReadingId: zod_1.z.uuid(),
    vitalCode: zod_1.z.enum(rpm_enums_js_1.vitalCode.enumValues),
    valueText: zod_1.z.string(),
    valueNumeric: zod_1.z.string().nullable(),
    valueUnit: zod_1.z.string().nullable(),
    unitResolution: zod_1.z.enum(rpm_enums_js_1.unitResolution.enumValues),
    plausibilityStatus: zod_1.z.enum(["PLAUSIBLE", "IMPLAUSIBLE"]),
    severityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
    classificationBandSetId: zod_1.z.uuid().nullable(),
    /**
     * The workup this correction belongs to.
     *
     * Acceptance rows 2 and 3 are the two shapes, and this field is how a caller tells them apart
     * WITHOUT asking a second question: before the obligation is fixed it is the SAME alert the
     * superseded reading belonged to — the workup gained the newer reading — and after it is fixed
     * it is a NEW one. Null where the superseded reading belonged to no workup at all (row 1).
     */
    rpmAlertId: zod_1.z.uuid().nullable(),
    /** True when `rpmAlertId` names an alert this correction opened rather than one it joined. */
    openedNewAlert: zod_1.z.boolean(),
    /** The alert the correction superseded, where there was one. Null in row 1. */
    supersededAlertId: zod_1.z.uuid().nullable(),
    /** True when the superseded alert was detached from an open held call (row 4, `RPM-SR-11`). */
    detachedFromHeldCall: zod_1.z.boolean(),
    /**
     * The three fields the dialog has to ANNOUNCE, carried back so it does not have to ask again.
     *
     * `RPM-101`'s announcement row: "The correction announces the old value and the new one together,
     * and, where a lane moved, says so — a correction that quietly changes a lane is the one a nurse
     * must not miss." A client that re-read the old row to say what it used to be would be reading a
     * row that now says `superseded_at`, one request later, and could say nothing at all if that read
     * failed.
     */
    supersededValueText: zod_1.z.string(),
    supersededSeverityBand: zod_1.z.enum(rpm_enums_js_1.severityBand.enumValues).nullable(),
    /** True when this correction's band sits on a different lane from the reading it supersedes. */
    laneMoved: zod_1.z.boolean(),
})
    .meta({
    id: "Correction",
    description: "The reading that now stands, and what its arrival did to the workup",
});
/**
 * The entry whose date and time is being corrected, named in the path (`RPM-101`'s Operator surface:
 * "correct the date and time — on the entry's one date and time, correcting every reading of the
 * entry together"). An opaque id, which is what `.claude/rules/phi.md` rule 2 permits in a path.
 */
exports.entryTimeCorrectionParamsSchema = zod_1.z.strictObject({ rpmAlertId: zod_1.z.uuid() });
/**
 * THE ENTRY'S ONE DATE AND TIME, and nothing else. The value, the unit and the vital are each
 * reading's own and are corrected one reading at a time; the moment is the entry's (`RPM-004`), so
 * it is corrected once and every current reading of the entry is superseded with it. The same text
 * formats a first entry carries, read in the building's zone (`RPM-021`).
 */
exports.entryTimeCorrectionInputSchema = zod_1.z
    .strictObject({
    takenAtLocalDate: zod_1.z.string().min(1).max(40),
    takenAtLocalTime: zod_1.z.string().min(1).max(40),
})
    .meta({ description: "The date and time every current reading of the entry was taken" });
exports.entryTimeCorrectionSchema = zod_1.z
    .strictObject({
    /** The entry's completion record the corrections belong to: the same one, or one they opened. */
    rpmAlertId: zod_1.z.uuid(),
    /** True when the entry was complete or its obligation fixed, so a new record opened (`RPM-006`). */
    openedNewAlert: zod_1.z.boolean(),
    /** One pair per current reading of the entry: the row that now stands and the one it supersedes. */
    readings: zod_1.z.array(zod_1.z.strictObject({
        vitalReadingId: zod_1.z.uuid(),
        correctsReadingId: zod_1.z.uuid(),
    })),
})
    .meta({
    id: "EntryTimeCorrection",
    description: "Every reading of the entry corrected to one new date and time",
});
//# sourceMappingURL=rpm-corrections.js.map